/**
 * Tailwind CSS implementation of the Message component pieces.
 * Uses Seeds message CSS classes from message.css (shipped out-of-band, like
 * button.css — see package.json build step + exports map). These pieces are
 * the pure CSS-class versions of the styled-components pieces in styles.ts and
 * are selected by MessageHybrid when no styled-system props are present.
 */

import React from "react";
import type { TypeMessageProps } from "./MessageTypes";
import { MESSAGE_DENSITIES } from "./constants";

// Utility for merging class names properly.
// Copied verbatim from ButtonTailwind.tsx — `cn` is not exported from
// @sproutsocial/seeds-react-utilities, so each Tailwind component keeps a local copy.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

// Pieces accept the same wide props the hybrid spreads in (mirrors how
// ButtonTailwind accepts the full TypeButtonProps). className/onClick/children
// and the density/borderColor/bg/indentContent/innerRef/qa props all already
// live on TypeMessageProps (via TypeBoxProps), so no destructuring changes are
// needed. The residual `...rest` is cast back to HTMLDivElement attributes at
// each spread so it is not checked against the intrinsic <div> — at runtime the
// Tailwind path only renders when no styled-system props are present, so `rest`
// never actually carries non-DOM props.
type TypeTailwindPieceProps = TypeMessageProps;

export const MessageRootTailwind = React.forwardRef<
  HTMLDivElement,
  TypeTailwindPieceProps
>(
  (
    {
      className,
      children,
      density,
      // Drop non-DOM props so they never leak onto the rendered element.
      borderColor: _borderColor,
      bg: _bg,
      indentContent: _indentContent,
      innerRef: _innerRef,
      qa: _qa,
      ...rest
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        "seeds-message",
        { "seeds-message--micro": density === MESSAGE_DENSITIES.MICRO },
        className
      )}
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  )
);

MessageRootTailwind.displayName = "Message.RootTailwind";

export const MessageHeaderTailwind = ({
  density,
  className,
  children,
  borderColor: _borderColor,
  bg: _bg,
  indentContent: _indentContent,
  innerRef: _innerRef,
  qa: _qa,
  ...rest
}: TypeTailwindPieceProps) => (
  <div
    className={cn(
      "seeds-message-header",
      `seeds-message-header--${density}`,
      className
    )}
    {...(rest as React.HTMLAttributes<HTMLDivElement>)}
  >
    {children}
  </div>
);

MessageHeaderTailwind.displayName = "Message.HeaderTailwind";

export const MessageBodyTailwind = ({
  density,
  indentContent,
  className,
  children,
  borderColor: _borderColor,
  bg: _bg,
  innerRef: _innerRef,
  qa: _qa,
  ...rest
}: TypeTailwindPieceProps) => (
  <div
    data-qa-message-body
    className={cn(
      "seeds-message-body",
      `seeds-message-body--${density}`,
      { "seeds-message-body--no-indent": !indentContent },
      className
    )}
    {...(rest as React.HTMLAttributes<HTMLDivElement>)}
  >
    {children}
  </div>
);

MessageBodyTailwind.displayName = "Message.BodyTailwind";

export const MessageFooterTailwind = ({
  density,
  indentContent,
  className,
  children,
  borderColor: _borderColor,
  bg: _bg,
  innerRef: _innerRef,
  qa: _qa,
  ...rest
}: TypeTailwindPieceProps) => (
  <div
    className={cn(
      "seeds-message-footer",
      `seeds-message-footer--${density}`,
      { "seeds-message-footer--no-indent": !indentContent },
      className
    )}
    {...(rest as React.HTMLAttributes<HTMLDivElement>)}
  >
    {children}
  </div>
);

MessageFooterTailwind.displayName = "Message.FooterTailwind";

export const MessageMetaTailwind = ({
  density,
  indentContent,
  className,
  children,
  onClick,
  borderColor: _borderColor,
  bg: _bg,
  innerRef: _innerRef,
  qa: _qa,
  ...rest
}: TypeTailwindPieceProps) => (
  <div
    className={cn(
      "seeds-message-meta",
      `seeds-message-meta--${density}`,
      {
        "seeds-message-meta--no-indent": !indentContent,
        "seeds-message-meta--clickable": !!onClick,
      },
      className
    )}
    onClick={onClick}
    {...(rest as React.HTMLAttributes<HTMLDivElement>)}
  >
    {children}
  </div>
);

MessageMetaTailwind.displayName = "Message.MetaTailwind";
